import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, Expand, Mail, Phone, Globe2, ShieldCheck, Bitcoin } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { http, photoUrl, carTitle, fmtPrice, fmtNum, trField, PHONE, PHONE_DISPLAY, EMAIL } from "../lib/api";
import { StatusBadge } from "../components/site/CarCard";
import { LogoMark } from "../components/site/Logo";
import { Lightbox } from "../components/site/Lightbox";
import { Reveal } from "../components/site/Reveal";

const SPEC_KEYS = ["year", "registration", "km", "cv", "kw", "engine_cc", "fuel", "gearbox", "body", "color", "doors", "seats", "owners", "plate", "vin"];

const useSpecs = (car) => {
  const { t, locale, lang } = useI18n();
  return SPEC_KEYS.filter((k) => car?.[k] !== null && car?.[k] !== undefined && car?.[k] !== "").map((k) => {
    const v = car[k];
    const val = {
      km: () => `${fmtNum(v, locale)} km`, cv: () => `${v} CV`, kw: () => `${v} kW`, engine_cc: () => `${fmtNum(v, locale)} cc`,
      color: () => trField(car, "color", lang),
      fuel: () => t.fuel[v] || v, gearbox: () => t.gearbox[v] || v, body: () => t.body[v] || v,
    }[k];
    return { k, label: t.spec[k], value: val ? val() : String(v) };
  });
};

const Gallery = ({ car, onOpen, idx, setIdx }) => {
  const photos = car.photos || [];
  const n = photos.length;
  if (!n) {
    return <div className="grid aspect-[16/10] place-items-center rounded-[28px] bg-[#EDEDEA]"><LogoMark className="h-20 w-20" /></div>;
  }
  return (
    <div>
      <div className="group relative aspect-[16/10] overflow-hidden rounded-[28px] bg-ink" data-testid="gallery-main">
        {photos.map((p, i) => (
          <motion.img key={p} src={photoUrl(p)} alt={`${carTitle(car)} ${i + 1}`}
            initial={false} animate={{ opacity: i === idx ? 1 : 0, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 h-full w-full cursor-zoom-in object-cover" onClick={() => onOpen(i)} />
        ))}
        {n > 1 && (
          <>
            <button onClick={() => setIdx((idx - 1 + n) % n)} className="gallery-btn left-4" data-testid="gallery-prev" aria-label="prev"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => setIdx((idx + 1) % n)} className="gallery-btn right-4" data-testid="gallery-next" aria-label="next"><ChevronRight className="h-5 w-5" /></button>
          </>
        )}
        <button onClick={() => onOpen(idx)} className="glass-chip absolute bottom-4 right-4" data-testid="gallery-expand">
          <Expand className="h-3.5 w-3.5" /> {idx + 1} / {n}
        </button>
      </div>
      {n > 1 && (
        <div className="mt-4 flex gap-3 overflow-x-auto pb-2" data-lenis-prevent data-testid="gallery-thumbs">
          {photos.map((p, i) => (
            <button key={p} onClick={() => setIdx(i)} data-testid={`gallery-thumb-${i}`}
              className={`relative h-20 w-28 shrink-0 overflow-hidden rounded-xl transition-all duration-300 ${i === idx ? "ring-2 ring-champagne ring-offset-2" : "opacity-60 hover:opacity-100"}`}>
              <img src={photoUrl(p)} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const InfoPanel = ({ car }) => {
  const { t, locale, lang } = useI18n();
  const version = trField(car, "version", lang);
  const d = t.detail;
  const subject = encodeURIComponent(`${d.emailSubject} ${carTitle(car)}${car.version ? ` ${car.version}` : ""}`);
  return (
    <div className="lg:sticky lg:top-28">
      <div className="flex items-center gap-3">
        <p className="font-mono text-xs uppercase tracking-[0.26em] text-champagne-dark">{car.make}</p>
        <StatusBadge status={car.status} />
      </div>
      <h1 className="mt-3 font-display text-3xl font-extrabold uppercase leading-[1] tracking-tight text-ink sm:text-4xl" data-testid="car-detail-title">{car.model || carTitle(car)}</h1>
      {version && <p className="mt-2 text-lg text-slate-700">{version}</p>}
      <p className="mt-8 font-display text-4xl font-extrabold text-ink" data-testid="car-detail-price">
        {car.price ? fmtPrice(car.price, locale) : <span className="text-2xl">{t.inv.priceOnRequest}</span>}
      </p>
      <div className="mt-8 rounded-[24px] bg-ink p-6 text-pearl">
        <p className="font-display text-lg font-bold uppercase">{d.askTitle}</p>
        <p className="mt-2 text-sm text-zinc-300">{d.askText}</p>
        <div className="mt-5 flex flex-col gap-3">
          <a href={`tel:${PHONE}`} className="btn-gold justify-center" data-testid="car-detail-call"><Phone className="h-4 w-4" /> {d.call} · {PHONE_DISPLAY}</a>
          <a href={`mailto:${EMAIL}?subject=${subject}`} className="btn-outline-light justify-center" data-testid="car-detail-email"><Mail className="h-4 w-4" /> {d.email}</a>
        </div>
      </div>
      <ul className="mt-6 space-y-3 text-sm text-slate-700">
        {[[Globe2, t.hero.stats[0]], [ShieldCheck, t.hero.stats[1]], [Bitcoin, t.hero.stats[2]]].map(([I, s]) => (
          <li key={s} className="flex items-center gap-3"><I className="h-4 w-4 text-champagne-dark" /> {s}</li>
        ))}
      </ul>
    </div>
  );
};

export default function CarDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const [car, setCar] = useState(undefined);
  const [idx, setIdx] = useState(0);
  const [box, setBox] = useState(null);
  const specs = useSpecs(car);
  const openBox = useCallback((i) => setBox(i), []);

  useEffect(() => {
    http.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setCar(null));
  }, [id]);

  if (car === undefined) return <div className="min-h-screen pt-40" />;
  if (car === null) {
    return (
      <main className="mx-auto min-h-[70vh] max-w-[1440px] px-5 pt-40 sm:px-8 lg:px-12" data-testid="car-not-found">
        <p className="font-display text-3xl font-bold uppercase text-ink">{t.detail.notFound}</p>
        <Link to="/#inventory" className="btn-ink mt-8 inline-flex"><ArrowLeft className="h-4 w-4" /> {t.detail.back}</Link>
      </main>
    );
  }

  const desc = car.description_i18n?.[lang] || car.description;

  return (
    <main className="mx-auto max-w-[1440px] px-5 pb-24 pt-28 sm:px-8 lg:px-12 lg:pt-32" data-testid="car-detail-page">
      <Link to="/#inventory" className="link-underline inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.24em] text-slate-700" data-testid="car-detail-back">
        <ArrowLeft className="h-4 w-4" /> {t.detail.back}
      </Link>
      <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-8">
          <Gallery car={car} onOpen={openBox} idx={idx} setIdx={setIdx} />
          {specs.length > 0 && (
            <Reveal className="mt-14">
              <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-ink">{t.detail.specs}</h2>
              <dl className="mt-6 grid grid-cols-2 overflow-hidden rounded-[22px] border border-ink/10 bg-white sm:grid-cols-3" data-testid="car-specs">
                {specs.map((s) => (
                  <div key={s.k} className="bg-white p-5 shadow-[0_0_0_0.5px_rgba(13,15,18,0.14)]" data-testid={`spec-${s.k}`}>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">{s.label}</dt>
                    <dd className={`mt-2 font-display text-lg font-bold text-ink ${s.k === "vin" || s.k === "plate" ? "break-all" : "break-words"}`}>{s.value}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          )}
          {desc && (
            <Reveal className="mt-14">
              <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-ink">{t.detail.description}</h2>
              <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-slate-700" data-testid="car-description">{desc}</p>
            </Reveal>
          )}
        </div>
        <div className="lg:col-span-4"><InfoPanel car={car} /></div>
      </div>
      {box !== null && <Lightbox photos={car.photos} start={box} onClose={() => setBox(null)} onChange={setIdx} />}
    </main>
  );
}
